import { type ComponentProps, useMemo } from 'react';
import { type TextLinkifyTags } from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { DurationUnit } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { sweetpassCheckoutContentfulDataFallback } from './sweetpassCheckoutContentfulDataFallback';

export const useSweetpassCheckoutContentfulData = (
  props: UseSweetpassCheckoutContentfulDataProps,
) => {
  const {
    isFreeTrialEnabled = false,
    isSwitchingPlans = false,
    planPrice,
    trialDuration = '',
    trialDurationSingular = '',
    billingFrequencyUnit = DurationUnit.Month,
    billingFrequencyMembershipName = '',
    nextBillingDate = '',
    requestedPlanRecurrence = '',
  } = props;

  const {
    isLoading,
    title: standardTitle,
    termsAndConditions: standardTermsAndConditions,
    agreementText: standardAgreementText,
    sweetpassProgramAgreementUrlDisplayName,
    sweetpassProgramAgreementUrl,
    cta,
    freeTrialTitle,
    freeTrialTermsAndConditions,
    freeTrialAgreementText,
    freeTrialCta,
    freeTrialNote,
    planSwitchTitle,
    planSwitchTermsAndConditions,
    planSwitchCta,
    subscriptionSwitchSuccessBanner,
  } = useContentfulDataWithFallback();

  const { formatDate, formatPrice } = useLocalizationContext();
  const price =
    planPrice && !Number.isNaN(planPrice)
      ? formatPrice(planPrice * 100, 'USD')
      : '';
  const date = formatDate(new Date());

  const ctaLabel = selectContent({
    isFreeTrialEnabled,
    isSwitchingPlans,
    regularContent: cta,
    freeTrialContent: freeTrialCta,
    switchingPlansContent: planSwitchCta,
    switchingFreeTrialPlansContent: planSwitchCta,
  });

  const content = useMemo(() => {
    const interpolateValues = (field: string) => {
      return field
        .replaceAll('{trialDuration}', trialDuration)
        .replaceAll('{trialDurationSingular}', trialDurationSingular)
        .replaceAll('{price}', price)
        .replaceAll('{date}', date)
        .replaceAll(
          '{billingFrequencyUnit}',
          billingFrequencyUnit?.toLowerCase(),
        )
        .replaceAll(
          '{billingFrequencyMembershipName}',
          billingFrequencyMembershipName,
        )
        .replaceAll('{nextBillingDate}', formatDate(nextBillingDate))
        .replaceAll('{switchDate}', formatDate(nextBillingDate))
        .replaceAll('{cta}', ctaLabel)
        .replaceAll('{requestedPlanRecurrence}', requestedPlanRecurrence);
    };

    const title = interpolateValues(
      selectContent({
        isFreeTrialEnabled,
        isSwitchingPlans,
        regularContent: standardTitle,
        freeTrialContent: freeTrialTitle,
        switchingPlansContent: planSwitchTitle,
        switchingFreeTrialPlansContent: planSwitchTitle,
      }),
    );

    const termsAndConditions = interpolateValues(
      selectContent({
        isFreeTrialEnabled,
        isSwitchingPlans,
        regularContent: standardTermsAndConditions,
        freeTrialContent: freeTrialTermsAndConditions,
        switchingPlansContent: planSwitchTermsAndConditions,
        switchingFreeTrialPlansContent: freeTrialTermsAndConditions,
      }),
    );

    const agreementText = interpolateValues(
      selectContent({
        isFreeTrialEnabled,
        isSwitchingPlans,
        regularContent: standardAgreementText,
        freeTrialContent: freeTrialAgreementText,
        switchingPlansContent: freeTrialAgreementText,
        switchingFreeTrialPlansContent: freeTrialAgreementText,
      }),
    );

    const agreementTextLinks: TextLinkifyTagsLinks = [
      {
        tag: 'sweetpassProgramAgreementUrl',
        description: sweetpassProgramAgreementUrlDisplayName,
        href: sweetpassProgramAgreementUrl,
        newTab: true,
        underline: true,
      },
    ];

    const interpolatedFreeTrialNote =
      isFreeTrialEnabled && trialDuration
        ? interpolateValues(freeTrialNote)
        : undefined;

    return {
      title,
      ctaLabel,
      termsAndConditions,
      agreementText,
      agreementTextLinks,
      freeTrialNote: interpolatedFreeTrialNote,
      subscriptionSwitchSuccessBanner: interpolateValues(
        subscriptionSwitchSuccessBanner,
      ),
    };
  }, [
    isFreeTrialEnabled,
    isSwitchingPlans,
    freeTrialTitle,
    standardTitle,
    freeTrialTermsAndConditions,
    standardTermsAndConditions,
    freeTrialAgreementText,
    standardAgreementText,
    trialDuration,
    freeTrialNote,
    ctaLabel,
    sweetpassProgramAgreementUrlDisplayName,
    sweetpassProgramAgreementUrl,
    trialDurationSingular,
    price,
    date,
    billingFrequencyUnit,
    billingFrequencyMembershipName,
    nextBillingDate,
    planSwitchTitle,
    planSwitchTermsAndConditions,
    requestedPlanRecurrence,
    subscriptionSwitchSuccessBanner,
    formatDate,
  ]);

  return { content, isLoading };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPE = 'sweetpassCheckoutPage';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function useContentfulDataWithFallback() {
  const { data: contentfulData, fetching: isLoading } =
    useContentfulContentTypeEntry<ContentfulSweetpassCheckoutPage>({
      contentType: CONTENT_TYPE,
      include: 2,
    });

  const requestedData = contentfulData?.fields;
  const fallbackData = sweetpassCheckoutContentfulDataFallback.fields;

  return {
    isLoading,
    title: requestedData?.title ?? fallbackData.title,
    termsAndConditions:
      requestedData?.termsAndConditionsV3 ?? fallbackData.termsAndConditionsV3,
    sweetpassProgramAgreementUrlDisplayName:
      requestedData?.sweetpassProgramAgreementUrlDisplayName ??
      fallbackData.sweetpassProgramAgreementUrlDisplayName,
    sweetpassProgramAgreementUrl:
      requestedData?.sweetpassProgramAgreementUrl ??
      fallbackData.sweetpassProgramAgreementUrl,
    agreementText: requestedData?.agreementText ?? fallbackData.agreementText,
    cta: requestedData?.cta ?? fallbackData.cta,
    freeTrialTitle:
      requestedData?.freeTrialTitle ?? fallbackData.freeTrialTitle,
    freeTrialTermsAndConditions:
      requestedData?.freeTrialTermsAndConditionsV3 ??
      fallbackData?.freeTrialTermsAndConditionsV3,
    freeTrialAgreementText:
      requestedData?.freeTrialAgreementText ??
      fallbackData.freeTrialAgreementText,
    freeTrialCta: requestedData?.freeTrialCta ?? fallbackData.freeTrialCta,
    freeTrialNote: requestedData?.freeTrialNote ?? fallbackData.freeTrialNote,
    planSwitchTitle:
      requestedData?.planSwitchTitle ?? fallbackData.planSwitchTitle,
    planSwitchCta: requestedData?.planSwitchCta ?? fallbackData.planSwitchCta,
    planSwitchTermsAndConditions:
      requestedData?.planSwitchTermsAndConditions ??
      fallbackData.planSwitchTermsAndConditions,
    subscriptionSwitchSuccessBanner:
      requestedData?.subscriptionSwitchSuccessBanner ??
      fallbackData.subscriptionSwitchSuccessBanner,
  };
}

function selectContent(props: ContentSelectionProps) {
  const {
    isFreeTrialEnabled,
    isSwitchingPlans,
    regularContent,
    freeTrialContent,
    switchingPlansContent,
    switchingFreeTrialPlansContent,
  } = props;

  if (isFreeTrialEnabled && isSwitchingPlans) {
    return switchingFreeTrialPlansContent;
  }

  if (isFreeTrialEnabled) {
    return freeTrialContent;
  }

  if (isSwitchingPlans) {
    return switchingPlansContent;
  }

  return regularContent;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassCheckoutContentfulDataProps = Readonly<{
  isFreeTrialEnabled: boolean;
  isSwitchingPlans?: boolean;
  planPrice?: number;
  trialDuration?: string;
  trialDurationSingular?: string;
  billingFrequencyUnit?: DurationUnit;
  billingFrequencyMembershipName?: string;
  nextBillingDate?: string;
  requestedPlanRecurrence?: string;
}>;

type ContentfulSweetpassCheckoutPage = Readonly<{
  title: string;
  termsAndConditionsV3: string;
  agreementText: string;
  sweetpassProgramAgreementUrlDisplayName: string;
  sweetpassProgramAgreementUrl: string;
  cta: string;
  freeTrialTitle: string;
  freeTrialTermsAndConditionsV3: string;
  freeTrialAgreementText: string;
  freeTrialCta: string;
  planSwitchCta: string;
  freeTrialNote: string;
  planSwitchTitle: string;
  planSwitchTermsAndConditions: string;
  subscriptionSwitchSuccessBanner: string;
}>;

type ContentSelectionProps = Readonly<{
  isFreeTrialEnabled: boolean;
  isSwitchingPlans: boolean;
  regularContent: string;
  freeTrialContent: string;
  switchingPlansContent: string;
  switchingFreeTrialPlansContent: string;
}>;

type TextLinkifyTagsLinks = ComponentProps<typeof TextLinkifyTags>['links'];
